/**
 * Kit (ConvertKit) Integration for Alera
 * Handles email marketing automation for new members and leads
 */

const KIT_API_KEY = process.env.REACT_APP_KIT_API_KEY;
const KIT_API_SECRET = process.env.REACT_APP_KIT_API_SECRET;
const KIT_API_BASE = 'https://api.convertkit.com/v3';
const KIT_STARTER_PACK_FORM_ID = '8911266';

/**
 * Check if Kit is configured
 */
export const isKitConfigured = () => {
  return !!(KIT_API_KEY && KIT_API_SECRET);
};

/**
 * Add a subscriber to Kit
 * @param {Object} data - Subscriber data
 * @param {string} data.email - Email address (required)
 * @param {string} data.firstName - First name
 * @param {string} data.lastName - Last name
 * @param {Array<string>} data.tags - Tags to apply (e.g., ['new-member', 'gluten-free'])
 * @param {Object} data.customFields - Custom fields (e.g., { fitness_level: 'beginner' })
 * @returns {Promise<Object>} - API response
 */
export const addSubscriber = async (data) => {
  if (!isKitConfigured()) {
    console.warn('Kit is not configured. Skipping subscriber addition.');
    return { success: false, error: 'Kit not configured' };
  }

  try {
    const { email, firstName, lastName, tags = [], customFields = {} } = data;

    if (!email) {
      throw new Error('Email is required');
    }

    // Add subscriber
    const subscriberData = {
      api_key: KIT_API_KEY,
      email,
      first_name: firstName || '',
      fields: customFields
    };

    const response = await fetch(`${KIT_API_BASE}/forms/${KIT_STARTER_PACK_FORM_ID}/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscriberData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to add subscriber');
    }

    const result = await response.json();
    const subscriberId = result.subscription?.subscriber?.id;

    // Add tags if provided
    if (tags.length > 0 && subscriberId) {
      await addTagsToSubscriber(subscriberId, tags);
    }

    console.log('Successfully added subscriber to Kit:', email);
    return { success: true, data: result };

  } catch (error) {
    console.error('Error adding subscriber to Kit:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add tags to a subscriber
 * @param {number} subscriberId - Subscriber ID
 * @param {Array<string>} tags - Array of tag names
 */
const addTagsToSubscriber = async (subscriberId, tags) => {
  try {
    for (const tagName of tags) {
      // First, get or create the tag
      const tagId = await getOrCreateTag(tagName);
      
      if (tagId) {
        // Then tag the subscriber
        await fetch(`${KIT_API_BASE}/tags/${tagId}/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            api_key: KIT_API_KEY,
            email: subscriberId
          })
        });
      }
    }
  } catch (error) {
    console.error('Error adding tags:', error);
  }
};

/**
 * Get or create a tag by name
 * @param {string} tagName - Tag name
 * @returns {Promise<number|null>} - Tag ID
 */
const getOrCreateTag = async (tagName) => {
  try {
    // Get all tags
    const response = await fetch(`${KIT_API_BASE}/tags?api_key=${KIT_API_KEY}`);
    const data = await response.json();
    
    // Find existing tag
    const existingTag = data.tags?.find(tag => tag.name.toLowerCase() === tagName.toLowerCase());
    
    if (existingTag) {
      return existingTag.id;
    }

    // Create new tag
    const createResponse = await fetch(`${KIT_API_BASE}/tags`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        api_secret: KIT_API_SECRET,
        tag: { name: tagName }
      })
    });

    const createData = await createResponse.json();
    return createData.tag?.id || null;

  } catch (error) {
    console.error('Error getting/creating tag:', error);
    return null;
  }
};

/**
 * Add a new member to Kit with their profile data
 * @param {Object} memberData - Member profile data from signup
 */
export const addNewMember = async (memberData) => {
  const { email, firstName, lastName, allergies = [], healthGoals = [], fitnessLevel } = memberData;

  // Build tags array
  const tags = ['new-member', 'paid-member'];
  
  // Add allergy tags
  if (allergies.length > 0) {
    allergies.forEach(allergy => {
      tags.push(`allergy-${allergy.toLowerCase().replace(/\s+/g, '-')}`);
    });
  }

  // Add fitness level tag
  if (fitnessLevel) {
    tags.push(`fitness-${fitnessLevel}`);
  }

  // Add health goal tags
  if (healthGoals.length > 0) {
    healthGoals.forEach(goal => {
      tags.push(`goal-${goal.toLowerCase().replace(/\s+/g, '-')}`);
    });
  }

  // Custom fields
  const customFields = {
    fitness_level: fitnessLevel || '',
    allergies: allergies.join(', '),
    health_goals: healthGoals.join(', ')
  };

  return await addSubscriber({
    email,
    firstName,
    lastName,
    tags,
    customFields
  });
};

/**
 * Add a lead magnet subscriber (starter pack request)
 * @param {Object} leadData - Lead data
 */
export const addLeadMagnetSubscriber = async (leadData) => {
  const { email, firstName, packType } = leadData;

  const tags = ['lead', 'starter-pack'];
  
  if (packType === 'recipe') {
    tags.push('recipe-pack');
  } else if (packType === 'workout') {
    tags.push('workout-pack');
  }

  return await addSubscriber({
    email,
    firstName,
    tags,
    customFields: {
      lead_magnet: packType || 'starter-pack'
    }
  });
};

export default {
  isKitConfigured,
  addSubscriber,
  addNewMember,
  addLeadMagnetSubscriber
};
